import {
  ArtifactInstance,
  ArtifactDef,
  AnalysisState,
  Rarity,
  GAME_CONFIG,
} from '../types';
import { getArtifactDef } from '../data/artifacts';

/** Ordered analysis pipeline. Each state advances to the next. */
const ANALYSIS_PIPELINE: AnalysisState[] = [
  'dirty',
  'cleaned',
  'examined',
  'dated',
  'cross_referenced',
  'documented',
];

/** Human-readable labels for each analysis step. */
const STEP_LABELS: Record<AnalysisState, string> = {
  dirty: 'Clean',
  cleaned: 'Examine',
  examined: 'Carbon Date',
  dated: 'Cross-Reference',
  cross_referenced: 'Document',
  documented: 'Complete',
};

/** Time cost for each analysis transition (from current state). */
const STEP_TIME_COST: Record<AnalysisState, number> = {
  dirty: 1,            // clean costs 1 time
  cleaned: 2,          // examine costs 2 time
  examined: 0,         // carbon date costs money, not time
  dated: 2,            // cross-reference costs 2 time
  cross_referenced: 1, // document costs 1 time
  documented: 0,
};

/** Multiplier for artifact value based on its analysis state. */
const ANALYSIS_VALUE_MULTIPLIER: Record<AnalysisState, number> = {
  dirty: 0.1,
  cleaned: 0.25,
  examined: 0.4,
  dated: 0.6,
  cross_referenced: 0.8,
  documented: 1.0,
};

/** Carbon dating cost by rarity. */
function carbonDateCost(rarity: Rarity): number {
  switch (rarity) {
    case 'common':
    case 'uncommon':
      return 200;
    case 'rare':
      return 300;
    case 'legendary':
    case 'mythical':
      return 500;
  }
}

export class ArtifactManager {
  inventory: ArtifactInstance[] = [];

  /** Add a newly discovered artifact to the inventory. */
  addArtifact(artifact: ArtifactInstance): void {
    this.inventory.push(artifact);
  }

  /** Get artifacts with optional filtering. */
  getArtifacts(filter?: {
    siteId?: string;
    analysisState?: AnalysisState;
    sold?: boolean;
    displayed?: boolean;
  }): ArtifactInstance[] {
    if (!filter) return this.inventory;

    return this.inventory.filter((a) => {
      if (filter.siteId !== undefined && a.siteId !== filter.siteId) return false;
      if (filter.analysisState !== undefined && a.analysisState !== filter.analysisState) return false;
      if (filter.sold !== undefined && a.sold !== filter.sold) return false;
      if (filter.displayed !== undefined && a.displayed !== filter.displayed) return false;
      return true;
    });
  }

  /** Find an artifact by its unique ID. */
  getByUid(uid: string): ArtifactInstance | undefined {
    return this.inventory.find((a) => a.uid === uid);
  }

  /** Get the static definition for an artifact instance. */
  getDef(artifact: ArtifactInstance): ArtifactDef | undefined {
    return getArtifactDef(artifact.defId);
  }

  /** Clean: dirty -> cleaned. Costs 1 time unit. */
  clean(uid: string): boolean {
    return this.tryAdvance(uid, 'dirty', 'cleaned');
  }

  /** Examine: cleaned -> examined. Costs 2 time units. */
  examine(uid: string): boolean {
    return this.tryAdvance(uid, 'cleaned', 'examined');
  }

  /**
   * Carbon date: examined -> dated.
   * Returns the money cost, or -1 if the artifact cannot be dated.
   */
  carbonDate(uid: string): number {
    const artifact = this.getByUid(uid);
    if (!artifact || artifact.analysisState !== 'examined') return -1;

    const def = getArtifactDef(artifact.defId);
    if (!def) return -1;

    const cost = carbonDateCost(def.rarity);
    artifact.analysisState = 'dated';
    artifact.analysisProgress = 0;
    return cost;
  }

  /** Cross-reference: dated -> cross_referenced. Costs 2 time units. */
  crossReference(uid: string): boolean {
    return this.tryAdvance(uid, 'dated', 'cross_referenced');
  }

  /** Document: cross_referenced -> documented. Costs 1 time unit. */
  document(uid: string): boolean {
    return this.tryAdvance(uid, 'cross_referenced', 'documented');
  }

  /** Get the time and money cost for the artifact's current analysis step. */
  getAnalysisCost(uid: string): { time: number; money: number } {
    const artifact = this.getByUid(uid);
    if (!artifact) return { time: 0, money: 0 };

    const state = artifact.analysisState;
    if (state === 'documented') return { time: 0, money: 0 };

    const time = STEP_TIME_COST[state];

    let money = 0;
    if (state === 'examined') {
      const def = getArtifactDef(artifact.defId);
      if (def) {
        money = carbonDateCost(def.rarity);
      }
    }

    return { time, money };
  }

  /** Get the human-readable name of the next analysis step, or null if fully analyzed. */
  getNextStep(uid: string): string | null {
    const artifact = this.getByUid(uid);
    if (!artifact) return null;
    if (artifact.analysisState === 'documented') return null;
    return STEP_LABELS[artifact.analysisState];
  }

  /** Advance analysis by one step (general purpose). Returns success and cost. */
  advanceAnalysis(uid: string): { success: boolean; cost: { time: number; money: number } } {
    const artifact = this.getByUid(uid);
    if (!artifact || artifact.analysisState === 'documented') {
      return { success: false, cost: { time: 0, money: 0 } };
    }

    const cost = this.getAnalysisCost(uid);
    const state = artifact.analysisState;

    // Carbon dating is a special case -- returns money cost
    if (state === 'examined') {
      const moneyCost = this.carbonDate(uid);
      if (moneyCost === -1) return { success: false, cost: { time: 0, money: 0 } };
      return { success: true, cost: { time: 0, money: moneyCost } };
    }

    const idx = ANALYSIS_PIPELINE.indexOf(state);
    if (idx < 0 || idx >= ANALYSIS_PIPELINE.length - 1) {
      return { success: false, cost: { time: 0, money: 0 } };
    }

    artifact.analysisState = ANALYSIS_PIPELINE[idx + 1];
    artifact.analysisProgress = 0;
    return { success: true, cost };
  }

  /** Mark an artifact as sold. */
  markSold(uid: string): void {
    const artifact = this.getByUid(uid);
    if (artifact) {
      artifact.sold = true;
      artifact.displayed = false;
      artifact.displaySlot = -1;
    }
  }

  /** Place an artifact on display at the given museum slot. */
  markDisplayed(uid: string, slot: number): void {
    const artifact = this.getByUid(uid);
    if (artifact) {
      artifact.displayed = true;
      artifact.displaySlot = slot;
    }
  }

  /** Remove an artifact from museum display. */
  removeFromDisplay(uid: string): void {
    const artifact = this.getByUid(uid);
    if (artifact) {
      artifact.displayed = false;
      artifact.displaySlot = -1;
    }
  }

  /**
   * Get the display name for an artifact based on its analysis state.
   *  - dirty: "Unknown Object"
   *  - cleaned: "Unidentified [icon]"
   *  - examined: "[Type] Artifact"
   *  - dated+: full name from definition
   */
  getDisplayName(artifact: ArtifactInstance): string {
    const def = getArtifactDef(artifact.defId);

    switch (artifact.analysisState) {
      case 'dirty':
        return 'Unknown Object';
      case 'cleaned':
        return `Unidentified ${def?.icon ?? '?'}`;
      case 'examined': {
        if (!def) return 'Unknown Artifact';
        const typeLabel = def.type.charAt(0).toUpperCase() + def.type.slice(1);
        return `${typeLabel} Artifact`;
      }
      case 'dated':
      case 'cross_referenced':
      case 'documented':
        return def?.name ?? 'Unknown Artifact';
    }
  }

  /**
   * Calculate the sale value of an artifact.
   * value = baseValue * conditionMultiplier * rarityMultiplier * analysisMultiplier
   */
  getValue(artifact: ArtifactInstance): number {
    const def = getArtifactDef(artifact.defId);
    if (!def) return 0;

    const condMul = GAME_CONFIG.conditionMultipliers[artifact.condition];
    const rarMul = GAME_CONFIG.rarityMultipliers[def.rarity];
    const analysisMul = ANALYSIS_VALUE_MULTIPLIER[artifact.analysisState];

    return Math.round(def.baseValue * condMul * rarMul * analysisMul);
  }

  /**
   * Calculate a museum appeal score for display purposes.
   * Combines the base appeal with condition and analysis bonuses.
   */
  getMuseumAppeal(artifact: ArtifactInstance): number {
    const def = getArtifactDef(artifact.defId);
    if (!def) return 0;

    let appeal = def.museumAppeal;

    // Condition bonus
    const condMul = GAME_CONFIG.conditionMultipliers[artifact.condition];
    appeal *= condMul;

    // Analysis bonus: fully documented artifacts are more appealing
    const stateIdx = ANALYSIS_PIPELINE.indexOf(artifact.analysisState);
    const analysisMul = 1 + stateIdx * 0.15; // up to 1.75x for documented
    appeal *= analysisMul;

    return Math.round(appeal * 10) / 10;
  }

  // ── Private helpers ──

  /**
   * Try to advance from `requiredState` to `nextState`.
   * Returns true on success, false if the artifact is not in the required state.
   */
  private tryAdvance(
    uid: string,
    requiredState: AnalysisState,
    nextState: AnalysisState,
  ): boolean {
    const artifact = this.getByUid(uid);
    if (!artifact || artifact.analysisState !== requiredState) return false;

    artifact.analysisState = nextState;
    artifact.analysisProgress = 0;
    return true;
  }
}
